import { useMemo, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import { Button, Field, Input, Modal, Radio } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';

export interface ScheduleProblem {
  path: string;
  message: string;
}

export interface ScheduleDialogProps {
  open: boolean;
  onClose(): void;
  campaignId: number;
  campaignName: string;
  /** IANA zone the time is chosen in — the scheduler's own. */
  timezone: string;
  recipientEstimate: number | null;
  needsApproval: boolean;
  /** `undefined` when the threshold could not be read; `null` = never needs approval. */
  approvalThreshold: number | null | undefined;
  /** "Takes about N days …" — computed by the builder from the estimate and the account's daily limit. */
  finishLabel: string | null;
  onScheduled(): void;
  /** `422 MARKETING_CAMPAIGN_INCOMPLETE` — the problems go back to the builder's sections. */
  onIncomplete(problems: ScheduleProblem[]): void;
}

/** Wall-clock parts of an instant in a zone. */
function partsIn(instant: number, timeZone: string) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(new Date(instant));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0');
  return { y: get('year'), m: get('month'), d: get('day'), h: get('hour'), mi: get('minute') };
}

/** `YYYY-MM-DD` + `HH:MM` read as wall time in `timeZone` → UTC epoch ms, or null. */
function zonedToUtc(date: string, time: string, timeZone: string): number | null {
  const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const tm = /^(\d{2}):(\d{2})$/.exec(time);
  if (!dm || !tm) return null;
  const wall = Date.UTC(Number(dm[1]), Number(dm[2]) - 1, Number(dm[3]), Number(tm[1]), Number(tm[2]));
  let guess = wall;
  for (let i = 0; i < 2; i += 1) {
    const p = partsIn(guess, timeZone);
    const seen = Date.UTC(p.y, p.m - 1, p.d, p.h, p.mi);
    guess += wall - seen;
  }
  return guess;
}

function todayIn(timeZone: string): { date: string; time: string } {
  const p = partsIn(Date.now() + 60 * 60 * 1000, timeZone);
  const pad = (n: number) => String(n).padStart(2, '0');
  return { date: `${p.y}-${pad(p.m)}-${pad(p.d)}`, time: `${pad(p.h)}:00` };
}

function safeZone(timeZone: string): string {
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone }).format(0);
    return timeZone;
  } catch {
    return 'UTC';
  }
}

export function ScheduleDialog({
  open,
  onClose,
  campaignId,
  campaignName,
  timezone,
  recipientEstimate,
  needsApproval,
  approvalThreshold,
  finishLabel,
  onScheduled,
  onIncomplete,
}: ScheduleDialogProps) {
  const zone = useMemo(() => safeZone(timezone), [timezone]);
  // Mounted fresh each time it opens (the builder renders it only while open),
  // so the initial state is the reset.
  const [start] = useState(() => todayIn(zone));
  const [mode, setMode] = useState<'now' | 'later'>('now');
  const [date, setDate] = useState(start.date);
  const [time, setTime] = useState(start.time);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [whenError, setWhenError] = useState<string | null>(null);

  const instant = mode === 'later' ? zonedToUtc(date, time, zone) : null;
  const overThreshold =
    typeof approvalThreshold === 'number' && recipientEstimate !== null && recipientEstimate > approvalThreshold;
  const willNeedApproval = needsApproval || overThreshold;
  const finish = finishLabel;

  async function submit() {
    setError(null);
    setWhenError(null);
    let scheduledAt: string | null = null;
    if (mode === 'later') {
      if (instant === null) {
        setWhenError('Enter a date and a time.');
        return;
      }
      if (instant <= Date.now()) {
        setWhenError('Choose a time in the future.');
        return;
      }
      scheduledAt = new Date(instant).toISOString();
    }
    setSaving(true);
    try {
      await api.scheduleMarketingCampaign(campaignId, { scheduledAt, timezone: zone });
      setSaving(false);
      onScheduled();
    } catch (caught) {
      setSaving(false);
      if (caught instanceof ApiError && caught.code === 'MARKETING_CAMPAIGN_INCOMPLETE') {
        onIncomplete((caught.details ?? []).map((d) => ({ path: d.path, message: d.message })));
        return;
      }
      if (caught instanceof ApiError) {
        setError(caught.formMessage ?? caught.message);
      } else {
        setError('Could not reach the server. The campaign was not scheduled.');
      }
    }
  }

  const confirmLabel = willNeedApproval
    ? 'Request approval'
    : mode === 'now'
      ? 'Send now'
      : 'Schedule';

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!saving) onClose();
      }}
      title={`Schedule ${campaignName}`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void submit()} loading={saving}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        <fieldset className="m-0 grid gap-2 border-0 p-0">
          <legend className="mb-1 text-xs font-medium text-fg-secondary">When</legend>
          <Radio
            name="schedule-mode"
            label="Send now"
            checked={mode === 'now'}
            onChange={() => setMode('now')}
            disabled={saving}
          />
          <Radio
            name="schedule-mode"
            label="At a date and time"
            checked={mode === 'later'}
            onChange={() => setMode('later')}
            disabled={saving}
          />
        </fieldset>
        {mode === 'later' && (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Date" error={whenError}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  type="date"
                  value={date}
                  disabled={saving}
                  invalid={Boolean(whenError)}
                  onChange={(e) => setDate(e.target.value)}
                />
              )}
            </Field>
            <Field label={`Time (${zone})`}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  type="time"
                  value={time}
                  disabled={saving}
                  onChange={(e) => setTime(e.target.value)}
                />
              )}
            </Field>
          </div>
        )}

        <ul className="m-0 grid list-none gap-1.5 rounded-md border border-border bg-surface-sunken p-3 text-xs text-fg-secondary">
          <li>
            {recipientEstimate === null
              ? 'Recipients are counted when it is scheduled.'
              : `About ${recipientEstimate.toLocaleString()} ${recipientEstimate === 1 ? 'recipient' : 'recipients'}.`}
          </li>
          {finish && <li>{finish}.</li>}
          {willNeedApproval ? (
            <li className="font-medium text-warning">
              {typeof approvalThreshold === 'number' && recipientEstimate !== null
                ? `Needs approval because ${recipientEstimate.toLocaleString()} is above the threshold of ${approvalThreshold.toLocaleString()}. Nothing sends until it is approved.`
                : 'Needs approval before anything sends.'}
            </li>
          ) : (
            <li>{mode === 'now' ? 'Sending starts within a minute.' : 'Sending starts at the chosen time.'}</li>
          )}
          {mode === 'later' && instant !== null && (
            <li className="text-fg-muted">{new Date(instant).toISOString().replace('T', ' ').slice(0, 16)} UTC</li>
          )}
        </ul>

        {error && (
          <Notice tone="danger" role="alert" className="[overflow-wrap:anywhere]">
            {error}
          </Notice>
        )}
      </div>
    </Modal>
  );
}
